/*
  ImportData.jsx
  ----------------
  Interface pour importer les données des OS.
*/
import { useState } from "react";
import {
  Card,
  Text,
  Group,
  SimpleGrid,
  Container,
  Button,
  FileButton,
  Badge,
  Stack,
  Alert,
  Progress,
  Code,
  Modal,
  Table,
  ScrollArea,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { uploadFile, get } from "../tools/Request";
import { useUser } from "../tools/Context";

// Les clés correspondent exactement aux intitulés d'en-tête attendus côté import.
const EXAMPLES = {
  supplier: {
    title: "Exemple — Fournisseurs",
    note: "Voici l’en-tête exacte attendue et une ligne de démonstration. Les colonnes supplémentaires seront ignorées, mais toutes ces colonnes doivent être présentes.",
    headers: [
      "Groupe ComFo",
      "Code Famille CTA",
      "Code Segment CTA",
      "Type frs",
      "Nom frs",
      "N° frs",
      "Numéro DUNS FRS",
      "Type site frs",
      "Raison sociale du site frs",
      "Siret-site-frs",
      "Adresse site frs",
      "Adresse complémentaire",
      "Code postal site frs",
      "Ville site frs",
      "N° compte bancaire FRS",
      "Intitulé compte bancaire frs",
      "Autoriser l'application de la taxe",
      "Autoriser la TVA intracommunautaire",
    ],
    row: {
      "Groupe ComFo": "text",
      "Code Famille CTA": "text",
      "Code Segment CTA": "text",
      "Type frs": "text",
      "Nom frs": "text",
      "N° frs": "text",
      "Numéro DUNS FRS": "text",
      "Type site frs": "text",
      "Raison sociale du site frs": "text",
      "Siret-site-frs": "text",
      "Adresse site frs": "text",
      "Adresse complémentaire": "text",
      "Code postal site frs": "text",
      "Ville site frs": "text",
      "N° compte bancaire FRS": "text",
      "Intitulé compte bancaire frs": "text",
      "Autoriser l'application de la taxe": "text",
      "Autoriser la TVA intracommunautaire": "text",
    },
  },
  market: {
    title: "Exemple — Marchés",
    note: "Importez d’abord les fournisseurs. Le N° Marché et le N° Fournisseur doivent correspondre à des entrées existantes.",
    headers: [
      "N° Marché",
      "Description du marché",
      "Entité opérationnelle Marché",
      "Direction pilote",
      "Pôle pilote",
      "Unité pilote",
      "Centre pilote",
      "Statut d'avancement du marché",
      "Date de création",
      "Nom de l'acheteur",
      "Direction d'imputation commande",
      "Gestionnaire marché",
      "N° Fournisseur",
      "Nom Fournisseur",
      "Siret FRS",
      "Adresse Site FRS",
      "Code postal Site FRS",
      "Marché en alerte",
      "Travaux / Fournitures",
      "Code  CTA",
      "Libellé CTA",
      "Début de validité",
      "Fin de validité",
      "Montant initial validé en devise",
      "Montant validé",
      "Montant cdé/appelé",
      "Montant réceptionné",
      "Montant facturé HT",
      "Montant restant à commander",
      "Mode  de consultation",
      "N° avenant RATP",
      "Taux consommation marché",
      "Taux conso exploit",
      "Taux conso invest",
      "Date dernier appel",
      "Durée passée (mois)",
      "Durée totale (mois)",
      "Taux d'appel / durée du marché",
      "Taux de réception / durée du marché",
      "Taux ancienneté",
      "Taux d'utilisation",
    ],
    row: {
      "N° Marché": "text",
      "Description du marché": "text",
      "Entité opérationnelle Marché": "text",
      "Direction pilote": "text",
      "Pôle pilote": "text",
      "Unité pilote": "text",
      "Centre pilote": "text",
      "Statut d'avancement du marché": "text",
      "Date de création": "01/01/2025",
      "Nom de l'acheteur": "text",
      "Direction d'imputation commande": "text",
      "Gestionnaire marché": "text",
      "N° Fournisseur": "text",
      "Nom Fournisseur": "text",
      "Siret FRS": "text",
      "Adresse Site FRS": "text",
      "Code postal Site FRS": "text",
      "Marché en alerte": "text",
      "Travaux / Fournitures": "text",
      "Code  CTA": "text",
      "Libellé CTA": "text",
      "Début de validité": "01/01/2025",
      "Fin de validité": "01/01/2025",
      "Montant initial validé en devise": "text",
      "Montant validé": "text",
      "Montant cdé/appelé": "text",
      "Montant réceptionné": "text",
      "Montant facturé HT": "text",
      "Montant restant à commander": "text",
      "Mode  de consultation": "text",
      "N° avenant RATP": "text",
      "Taux consommation marché": "text",
      "Taux conso exploit": "text",
      "Taux conso invest": "text",
      "Date dernier appel": "01/01/2025",
      "Durée passée (mois)": "text",
      "Durée totale (mois)": "text",
      "Taux d'appel / durée du marché": "text",
      "Taux de réception / durée du marché": "text",
      "Taux ancienneté": "text",
      "Taux d'utilisation": "text",
    },
  },
  order: {
    title: "Exemple — Commandes / OS",
    note: "Importez d’abord les marchés. Les clés (N° cde/appel + N° ligne cde) doivent être présentes ; le N° marché doit exister.",
    headers: [
      "N° cde/appel",
      "Description cde/appel",
      "Source de la commande",
      "Entité opérationnelle",
      "Réf. affaire cde/appel",
      "Mode consultation",
      "Date création cde",
      "Date fermeture définitive",
      "Date du besoin de livraison de ligne",
      "Date dernière réception",
      "Délai d'exécution des travaux cde/appel",
      "Statut d'avancement",
      "Statut d'approbation",
      "Indice Annulation cde",
      "Indice Invest/exploitation cde/appel",
      "N° DA",
      "Direction pilote DA",
      "Emetteur DA",
      "Prescripteur DA",
      "N° marché",
      "Numéro fournisseur",
      "SIRET",
      "Numéro Siren",
      "Adresse Site",
      "Ville Site",
      "Code postal du Site",
      "Compte",
      "Société",
      "Centre",
      "Activité",
      "Equipement",
      "Centre CF",
      "Destination",
      "Nom fournisseur",
      "Code filiale Fournisseur",
      "Segt CTA fournisseur",
      "Direction pilote",
      "Pôle pilote",
      "Unité pilote",
      "Centre pilote",
      "Code saisisseur cde/appel",
      "libellé Saisisseur cde/appel",
      "Nom acheteur",
      "Demandeur du besoin",
      "Destinataire livraison cde/appel",
      "Dir saisie",
      "Direction prescripteur cde/appel",
      "Etablissement prescripteur Cde/appel",
      "Centre prescripteur",
      "Libellé Centre prescripteur",
      "Direction CdP",
      "Direction imputation",
      "Pôle imputation",
      "Unité imputation",
      "Centre imputation",
      "Facture reçue",
      "Facture bloquée",
      "N° ligne cde",
      "Description ligne cde",
      "Date de création ligne cde",
      "CTA ligne cde",
      "Qté commandée",
      "Prix unitaire",
      "N° Ligne livr.",
      "N° Ligne imput",
      "Clé imputation cde/appel",
      "Montant commandé",
      "Montant réceptionné",
      "Montant  HT facturé",
      "Ecart facturation",
      "Reste à réceptionner",
      "CAP potentielle",
    ],
    row: {
      "N° cde/appel": "text",
      "Description cde/appel": "text",
      "Source de la commande": "text",
      "Entité opérationnelle": "text",
      "Réf. affaire cde/appel": "text",
      "Mode consultation": "text",
      "Date création cde": "10/02/2025",
      "Date fermeture définitive": "text",
      "Date du besoin de livraison de ligne": "20/02/2025",
      "Date dernière réception": "text",
      "Délai d'exécution des travaux cde/appel": "text",
      "Statut d'avancement": "text",
      "Statut d'approbation": "text",
      "Indice Annulation cde": "text",
      "Indice Invest/exploitation cde/appel": "text",
      "N° DA": "text",
      "Direction pilote DA": "text",
      "Emetteur DA": "text",
      "Prescripteur DA": "text",
      "N° marché": "text",
      "Numéro fournisseur": "text",
      SIRET: "text",
      "Numéro Siren": "text",
      "Adresse Site": "text",
      "Ville Site": "text",
      "Code postal du Site": "text",
      Compte: "text",
      Société: "text",
      Centre: "text",
      Activité: "text",
      Equipement: "text",
      "Centre CF": "text",
      Destination: "text",
      "Nom fournisseur": "text",
      "Code filiale Fournisseur": "text",
      "Segt CTA fournisseur": "text",
      "Direction pilote": "text",
      "Pôle pilote": "text",
      "Unité pilote": "text",
      "Centre pilote": "text",
      "Code saisisseur cde/appel": "text",
      "libellé Saisisseur cde/appel": "text",
      "Nom acheteur": "text",
      "Demandeur du besoin": "text",
      "Destinataire livraison cde/appel": "text",
      "Dir saisie": "text",
      "Direction prescripteur cde/appel": "text",
      "Etablissement prescripteur Cde/appel": "text",
      "Centre prescripteur": "text",
      "Libellé Centre prescripteur": "text",
      "Direction CdP": "text",
      "Direction imputation": "text",
      "Pôle imputation": "text",
      "Unité imputation": "text",
      "Centre imputation": "text",
      "Facture reçue": "text",
      "Facture bloquée": "text",
      "N° ligne cde": "text",
      "Description ligne cde": "text",
      "Date de création ligne cde": "text",
      "CTA ligne cde": "text",
      "Qté commandée": "text",
      "Prix unitaire": "text",
      "N° Ligne livr.": "text",
      "N° Ligne imput": "text",
      "Clé imputation cde/appel": "text",
      "Montant commandé": "text",
      "Montant réceptionné": "text",
      "Montant  HT facturé": "text",
      "Ecart facturation": "text",
      "Reste à réceptionner": "text",
      "CAP potentielle": "text",
    },
  },
};

export default function ImportData() {
  const [marketFile, setMarketFile] = useState(null);
  const [orderFile, setOrderFile] = useState(null);
  const [supplierFile, setSupplierFile] = useState(null);
  const [loading, setLoading] = useState({});
  const [success, setSuccess] = useState({ market: false, order: false, supplier: false });
  const [error, setError] = useState({ market: null, order: null, supplier: null });
  const [counts, setCounts] = useState({ market: null, order: null, supplier: null });
  const [exampleOpen, setExampleOpen] = useState(false);
  const [exampleKind, setExampleKind] = useState(null);
  const { token } = useUser();

  const pollStatus = async (kind, jobId) => {
    try {
      const s = await get(`import/status/${jobId}`, null, token);
      if (!s?.ok) throw new Error(s?.error || "Erreur de statut");

      if (s.status === "error") throw new Error(s.error || "Erreur lors du traitement");

      setLoading((l) => ({ ...l, [`${kind}Progress`]: s.progress ?? 0 }));
      if (s.counts) setCounts((c) => ({ ...c, [kind]: s.counts }));

      if (s.status === "done") {
        setSuccess((x) => ({ ...x, [kind]: true }));
        setLoading((l) => ({ ...l, [kind]: false, [`${kind}Progress`]: 100 }));
        return;
      }

      setTimeout(() => pollStatus(kind, jobId), 1200);
    } catch (e) {
      const msg = e?.message || e?.error || "Erreur inconnue";
      setError((er) => ({ ...er, [kind]: msg }));
      setLoading((l) => ({ ...l, [kind]: false }));
    }
  };

  const doUpload = async (kind) => {
    try {
      setError((e) => ({ ...e, [kind]: null }));
      setSuccess((s) => ({ ...s, [kind]: false }));
      setCounts((c) => ({ ...c, [kind]: null }));
      setLoading((l) => ({ ...l, [kind]: true, [`${kind}Progress`]: 0 }));

      const file = kind === "market" ? marketFile : kind === "order" ? orderFile : supplierFile;
      if (!file) throw new Error("Veuillez sélectionner un fichier .xlsx");

      const fd = new FormData();
      fd.append("file", file);

      const { jobId } = await uploadFile(`import/${kind}`, fd, token);
      if (!jobId) throw new Error("jobId manquant");

      if (kind === "market") setMarketFile(null);
      if (kind === "order") setOrderFile(null);
      if (kind === "supplier") setSupplierFile(null);

      await pollStatus(kind, jobId);
    } catch (e) {
      const msg = e?.message || e?.error || "Erreur inconnue";
      setError((er) => ({ ...er, [kind]: msg }));
      setLoading((l) => ({ ...l, [kind]: false }));
    }
  };

  const Status = ({ kind }) => (
    <Stack gap={6} style={{ flex: 1 }}>
      {typeof loading[`${kind}Progress`] === "number" && loading[kind] && (
        <>
          <Progress value={loading[`${kind}Progress`]} />
          <Text size="sm" c="dimmed">
            Progression : {loading[`${kind}Progress`]}%
          </Text>
        </>
      )}

      {counts[kind] && (
        <Code block mt={4}>
          {JSON.stringify(counts[kind], null, 2)}
        </Code>
      )}

      {success[kind] && <Badge color="green">Succès</Badge>}
      {error[kind] && (
        <Text size="sm" c="red">
          {error[kind]}
        </Text>
      )}
    </Stack>
  );

  const Uploader = ({ kind, title, desc, file, setFile }) => (
    <Card shadow="md" padding="xl" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Text fw={600}>{title}</Text>
      </Group>
      <Text mb="md" size="sm" c="dimmed">
        {desc}
      </Text>
      <Stack gap="sm">
        <Group wrap="wrap">
          <FileButton onChange={setFile} accept=".xlsx">
            {(props) => <Button {...props}>Choisir un fichier</Button>}
          </FileButton>
          <Button
            variant="light"
            onClick={() => {
              setExampleKind(kind);
              setExampleOpen(true);
            }}
          >
            Voir un exemple
          </Button>
          {file && <Text size="sm">{file.name}</Text>}
        </Group>
        <Group align="center">
          <Button onClick={() => doUpload(kind)} loading={!!loading[kind]} disabled={!file}>
            Envoyer
          </Button>
          <Status kind={kind} />
        </Group>
      </Stack>
    </Card>
  );

  const ExampleModal = () => {
    const cfg = exampleKind ? EXAMPLES[exampleKind] : null;
    const headers = cfg?.headers ?? [];
    const row = cfg?.row ?? {};

    return (
      <Modal
        opened={exampleOpen}
        onClose={() => setExampleOpen(false)}
        title={cfg?.title || "Exemple"}
        size="xl"
        centered
      >
        {cfg?.note && (
          <Text size="sm" c="dimmed" mb="sm">
            {cfg.note}
          </Text>
        )}

        <ScrollArea h={360}>
          <Table highlightOnHover withTableBorder withColumnBorders>
            <Table.Tbody>
              {headers.map((h) => (
                <Table.Tr key={h}>
                  <Table.Th w="30%">{h}</Table.Th>
                  <Table.Td>{row[h] ?? ""}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Modal>
    );
  };

  return (
    <Container size="lg" mt="xl">
      <Alert
        icon={<IconAlertTriangle size={20} />}
        title="Attention"
        color="yellow"
        variant="light"
        mb="xl"
        radius="md"
      >
        <Text>Importez uniquement les fichiers provenant de l’extraction, et dans l’ordre suivant :</Text>
        <Text mt={4}>
          <strong>1️⃣ Fournisseurs</strong> → <strong>2️⃣ Marchés</strong> → <strong>3️⃣ Commandes</strong>
        </Text>
      </Alert>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
        <Uploader
          kind="supplier"
          title="Importer les fournisseurs"
          desc="Insérez le fichier au format XLSX contenant les informations des fournisseurs."
          file={supplierFile}
          setFile={setSupplierFile}
        />
        <Uploader
          kind="market"
          title="Importer les marchés"
          desc="Insérez le fichier au format XLSX contenant les informations des marchés."
          file={marketFile}
          setFile={setMarketFile}
        />
        <Uploader
          kind="order"
          title="Importer les commandes"
          desc="Insérez le fichier au format XLSX contenant les informations des commandes."
          file={orderFile}
          setFile={setOrderFile}
        />
      </SimpleGrid>
      <ExampleModal />
    </Container>
  );
}
